import { DialogWrapper } from '@/components/common/DialogWrapper';
import {
  matchesFilterByNameAndEmail,
  sortByNameAndEmail,
} from '@/components/common/FilterButtons';
import { Input } from '@/components/common/Input';
import { Pagination } from '@/components/common/Pagination';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import type { User } from '@/domain/User';
import { useState } from 'react';

interface ModifyRelationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: (User & { clinic?: { id: number; name: string } }) | null;
  userType: 'Doctor' | 'Customer';
  availableUsers: Array<User & { clinic?: { id: number; name: string } }>;
  assignedUsers: Array<User & { clinic?: { id: number; name: string } }>;
  onAssign: (userId: number) => void;
  onUnassign: (userId: number) => void;
}

export function ModifyRelationsDialog({
  open,
  onOpenChange,
  selectedUser,
  userType,
  availableUsers,
  assignedUsers,
  onAssign,
  onUnassign,
}: ModifyRelationsDialogProps) {
  const [availableFilter, setAvailableFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [availablePage, setAvailablePage] = useState(1);
  const [assignedPage, setAssignedPage] = useState(1);

  const perPage = 5;
  const targetLabel = userType === 'Doctor' ? 'Patient' : 'Doctor';

  const fullName = (u: User) => `${u.name} ${u.surname}`;
  const userEmail = (u: User) => u.email;

  const filteredAvailable = availableUsers.filter((user) =>
    matchesFilterByNameAndEmail(
      availableFilter,
      fullName(user),
      userEmail(user),
    ),
  );
  const sortedAvailable = sortByNameAndEmail(
    filteredAvailable,
    fullName,
    userEmail,
  );

  const filteredAssigned = assignedUsers.filter((user) =>
    matchesFilterByNameAndEmail(
      assignedFilter,
      fullName(user),
      userEmail(user),
    ),
  );
  const sortedAssigned = sortByNameAndEmail(
    filteredAssigned,
    fullName,
    userEmail,
  );

  const availableTotal = sortedAvailable.length;
  const availableLastPage = Math.ceil(availableTotal / perPage) || 1;
  const availableStart = (availablePage - 1) * perPage;
  const paginatedAvailable = sortedAvailable.slice(
    availableStart,
    availableStart + perPage,
  );

  const assignedTotal = sortedAssigned.length;
  const assignedLastPage = Math.ceil(assignedTotal / perPage) || 1;
  const assignedStart = (assignedPage - 1) * perPage;
  const paginatedAssigned = sortedAssigned.slice(
    assignedStart,
    assignedStart + perPage,
  );

  if (!selectedUser) return null;

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title={`Create relations for ${selectedUser.name} ${selectedUser.surname}`}
      maxWidth="4xl"
    >
      <div className="flex flex-col gap-8 md:flex-row">
        <div className="w-full min-w-0 md:w-1/2">
          <h3 className="text-secondary mb-4 text-2xl font-light">
            Assign New
          </h3>

          <Input
            id="available-filter"
            label="Filter by Name or Email"
            placeholder="Filter by Name or Email"
            className="bg-white text-base"
            value={availableFilter}
            onChange={(e) => {
              setAvailableFilter(e.target.value);
              setAvailablePage(1);
            }}
          />

          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="border-none hover:bg-transparent">
                <TableHead className="w-12"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedAvailable.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center text-xs text-slate-500 md:text-base"
                  >
                    No {targetLabel.toLowerCase()}s available
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAvailable.map((user, index) => (
                  <TableRow
                    key={user.id}
                    className="border-none hover:bg-transparent"
                  >
                    <TableCell>{availableStart + index + 1}</TableCell>
                    <TableCell className="truncate">
                      {user.name} {user.surname}
                    </TableCell>
                    <TableCell>
                      <RelationActionButton onClick={() => onAssign(user.id)}>
                        Assign
                      </RelationActionButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {availableTotal > perPage && (
            <Pagination
              layout="column"
              currentPage={availablePage}
              lastPage={availableLastPage}
              total={availableTotal}
              perPage={perPage}
              onPageChange={setAvailablePage}
            />
          )}
        </div>

        <div className="w-full min-w-0 md:w-1/2">
          <h3 className="text-secondary mb-4 text-2xl font-light">
            Un-assign Current
          </h3>

          <Input
            id="assigned-filter"
            label="Filter by Name or Email"
            placeholder="Filter by Name or Email"
            className="w-full bg-white"
            value={assignedFilter}
            onChange={(e) => {
              setAssignedFilter(e.target.value);
              setAssignedPage(1);
            }}
          />

          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="border-none hover:bg-transparent">
                <TableHead className="w-12"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedAssigned.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center text-xs text-slate-500 md:text-base"
                  >
                    No assigned {targetLabel.toLowerCase()}s
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAssigned.map((user, index) => (
                  <TableRow
                    key={user.id}
                    className="border-none hover:bg-transparent"
                  >
                    <TableCell>{assignedStart + index + 1}</TableCell>
                    <TableCell className="truncate">
                      {user.name} {user.surname}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => onUnassign(user.id)}
                        className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
                      >
                        Un-assign
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {assignedTotal > perPage && (
            <Pagination
              layout="column"
              currentPage={assignedPage}
              lastPage={assignedLastPage}
              total={assignedTotal}
              perPage={perPage}
              onPageChange={setAssignedPage}
            />
          )}
        </div>
      </div>
    </DialogWrapper>
  );
}
